import { Switch as BaseSwitch } from '@base-ui/react/switch';
import { cn } from '@/shared/lib/cn';
import { cx } from './lib/tv';
import { renderIcon, type IconLike } from './icon';

export interface SwitchProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  /** `md` 40×24 (default) · `sm` 36×20. */
  size?: 'sm' | 'md';
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
  /** Inline label to the right (13/18). For rows with a description use `SettingsToggleRow`. */
  label?: React.ReactNode;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  className?: string;
}

/**
 * Switch — kit 67:11994 (spec §4.12): track White/10 (off) / `#3576ec` (on), white knob + shadow-knob, inset 2.
 * Disabled: track White/10, knob White/10.
 */
export function Switch({ size = 'md', label, className, onCheckedChange, ...props }: SwitchProps) {
  const control = (
    <BaseSwitch.Root
      {...props}
      aria-label={label ? undefined : props['aria-label']}
      onCheckedChange={(next) => onCheckedChange?.(next)}
      className={cn(
        'relative inline-flex shrink-0 cursor-pointer items-center rounded-full bg-field p-2 transition-colors duration-150 ease-standard',
        'data-checked:bg-control data-disabled:cursor-not-allowed data-disabled:bg-field',
        size === 'md' ? 'h-24 w-40' : 'h-20 w-36',
        !label && className,
      )}
    >
      <BaseSwitch.Thumb
        className={cn(
          'block rounded-full bg-white shadow-knob transition-transform duration-150 ease-standard',
          'data-disabled:bg-field data-disabled:shadow-none',
          size === 'md' ? 'size-20 data-checked:translate-x-16' : 'size-16 data-checked:translate-x-16',
        )}
      />
    </BaseSwitch.Root>
  );
  if (!label) return control;
  return (
    <label
      className={cn(
        'inline-flex items-center gap-10 t-field text-fg',
        props.disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        className,
      )}
    >
      {control}
      {label}
    </label>
  );
}

export interface SettingsToggleRowProps extends Omit<SwitchProps, 'label' | 'size'> {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Optional leading glyph (16, White/60). */
  icon?: IconLike;
}

/**
 * Setting row (559:17445 "Ixcham jadval", 516:12991 "Faol xodim"): `bg-field` r8, padding 8/12 (12 without a
 * description), gap 12; title 400 13/18 white; description 400 11/15 White/60; Switch md on the right.
 * The whole row is the label of the switch.
 */
export function SettingsToggleRow({ title, description, icon, className, disabled, ...switchProps }: SettingsToggleRowProps) {
  return (
    <label
      className={cx(
        'flex w-full min-w-0 items-center gap-12 rounded-8 bg-field px-12 transition-colors duration-150',
        description ? 'py-8' : 'py-12',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        className,
      )}
    >
      {renderIcon(icon, 'size-16 shrink-0 text-icon')}
      <span className="flex min-w-0 flex-1 flex-col gap-2">
        <span className="t-field text-fg">{title}</span>
        {description && <span className="text-11 text-fg-secondary">{description}</span>}
      </span>
      <Switch {...switchProps} disabled={disabled} />
    </label>
  );
}
